import React from 'react';
import { useLanguage } from '@/state/language/LanguageProvider';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Link } from 'react-router-dom';
import { 
  UsersIcon, 
  ShieldCheckIcon, 
  KeyIcon, 
  UserGroupIcon,
  CogIcon,
  DocumentTextIcon,
  EyeIcon,
  LockClosedIcon
} from '@heroicons/react/24/outline';

const AccessManagementPage: React.FC = () => {
  const { t } = useLanguage();

  const accessManagementModules = [
    {
      title: t('pages.userManagement'),
      description: t('accessManagement.userManagementDescription'),
      icon: UsersIcon,
      href: '/user-management',
      color: 'bg-blue-500',
      permissions: ['USER_MANAGEMENT']
    },
    {
      title: t('pages.roleManagement'),
      description: t('accessManagement.roleManagementDescription'),
      icon: ShieldCheckIcon,
      href: '/role-management',
      color: 'bg-green-500',
      permissions: ['ROLE_MANAGEMENT']
    },
    {
      title: t('pages.permissionManagement'),
      description: t('accessManagement.permissionManagementDescription'),
      icon: KeyIcon,
      href: '/permission-management',
      color: 'bg-purple-500',
      permissions: ['PERMISSION_MANAGEMENT']
    },
    {
      title: t('pages.userGroups'),
      description: t('accessManagement.userGroupsDescription'),
      icon: UserGroupIcon,
      href: '/user-groups',
      color: 'bg-orange-500',
      permissions: ['USER_GROUP_MANAGEMENT']
    },
    {
      title: t('pages.roleAssignment'),
      description: t('accessManagement.roleAssignmentDescription'),
      icon: CogIcon,
      href: '/role-assignment',
      color: 'bg-indigo-500',
      permissions: ['ROLE_ASSIGNMENT']
    },
    {
      title: t('pages.permissionAssignment'),
      description: t('accessManagement.permissionAssignmentDescription'),
      icon: DocumentTextIcon,
      href: '/permission-assignment',
      color: 'bg-pink-500',
      permissions: ['PERMISSION_ASSIGNMENT']
    },
    {
      title: t('pages.accessPolicies'),
      description: t('accessManagement.accessPoliciesDescription'),
      icon: LockClosedIcon,
      href: '/access-policies',
      color: 'bg-red-500',
      permissions: ['ACCESS_POLICY_MANAGEMENT']
    },
    {
      title: t('pages.accessReviews'),
      description: t('accessManagement.accessReviewsDescription'),
      icon: EyeIcon,
      href: '/access-reviews',
      color: 'bg-teal-500',
      permissions: ['ACCESS_REVIEW_MANAGEMENT']
    }
  ];

  return (
    <div className="min-h-screen bg-[var(--color-secondary-bg)]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-[var(--color-surface-text)] mb-2">
            {t('accessManagement.title')}
          </h1>
          <p className="text-lg text-[var(--color-secondary-text)]">
            {t('accessManagement.subtitle')}
          </p>
        </div>

        {/* Access Management Modules Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {accessManagementModules.map((module, index) => (
            <Card key={index} className="hover:shadow-lg transition-shadow duration-200">
              <div className="p-6">
                <div className="flex items-center mb-4">
                  <div className={`p-3 rounded-lg ${module.color} text-white`}>
                    <module.icon className="h-6 w-6" />
                  </div>
                  <div className="ml-4">
                    <h3 className="text-lg font-semibold text-[var(--color-surface-text)]">
                      {module.title}
                    </h3>
                  </div>
                </div>
                
                <p className="text-[var(--color-secondary-text)] mb-4 text-sm">
                  {module.description}
                </p>
                
                <Link to={module.href}>
                  <Button 
                    variant="outline" 
                    className="w-full"
                  >
                    {t('common.access')}
                  </Button>
                </Link>
              </div>
            </Card>
          ))}
        </div>

        {/* Quick Actions */}
        <div className="mt-12">
          <h2 className="text-2xl font-bold text-[var(--color-surface-text)] mb-6">
            {t('accessManagement.quickActions')}
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card className="p-6">
              <h3 className="text-lg font-semibold text-[var(--color-surface-text)] mb-2">
                {t('accessManagement.createUser')}
              </h3>
              <p className="text-[var(--color-secondary-text)] mb-4 text-sm">
                {t('accessManagement.createUserDescription')}
              </p>
              <Link to="/user-management/create">
                <Button variant="primary" className="w-full">
                  {t('common.create')}
                </Button>
              </Link>
            </Card>

            <Card className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                {t('accessManagement.createRole')}
              </h3>
              <p className="text-gray-600 mb-4 text-sm">
                {t('accessManagement.createRoleDescription')}
              </p>
              <Link to="/role-management/create">
                <Button variant="primary" className="w-full">
                  {t('common.create')}
                </Button>
              </Link>
            </Card>

            <Card className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                {t('accessManagement.auditAccess')}
              </h3>
              <p className="text-gray-600 mb-4 text-sm">
                {t('accessManagement.auditAccessDescription')}
              </p>
              <Link to="/access-reviews">
                <Button variant="primary" className="w-full">
                  {t('common.audit')}
                </Button>
              </Link>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AccessManagementPage;
